/**
 * The Academics → Exams (cycles) list page's filter + sort declaration — a
 * consumer of the generic filter toolkit (`components/filters`), mirroring
 * `studentsFilterConfig` / `withdrawalsFilterConfig`.
 *
 * Facets exposed on the bar:
 *   - search    → `q`                     cycle-name search.
 *   - status    → `status`                multi-select over draft/active/
 *                                          marks_locked/archived. `defaultValue`
 *                                          is `[]` (= All) and `allEncoding`
 *                                          is `'omit'`: the backend treats an
 *                                          OMITTED status as "all", so "All"
 *                                          simply drops the param.
 *   - type      → `type`                  multi-select over monthly/half_yearly/
 *                                          annual/other; `omit` encoding.
 *   - start     → `startFrom` / `startTo` date range over the cycle's
 *                                          `startDate`.
 *
 * NOTE: `academicYear` stays a sibling single-value control (NOT a toolkit
 * facet): the backend filters it with a single exact `eq`, so a comma-joined
 * multi-select would break it. The route feeds that control's value into the
 * same single query alongside `apiParams` (see the withdrawals page for the
 * same pattern). It is a free-text field (arbitrary year strings like
 * "2026-2027"), so it is deliberately kept as an input rather than a select.
 *
 * Sorts map to the backend's allow-listed `sort` fields
 * ({startDate, academicYear, createdAt}). The default (first entry) is
 * "Latest start" = startDate:desc — the page's pre-existing implicit order.
 *
 * Sortable column header: Dates (startDate) — both directions are registered so
 * the header can toggle desc↔asc without snapping back to the default sort.
 * academicYear (desc-only) and createdAt (no column) stay dropdown-only.
 */

import { z } from 'zod';
import type { ListFilterConfig, SortColumnBinding } from '../filters';

export const examsFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'search',
      key: 'q',
      label: 'Search',
      placeholder: 'Search by cycle name',
    },
    {
      kind: 'multi-select',
      key: 'status',
      label: 'Status',
      group: 'filters',
      // Absent status = all on the backend, so the default is "All" (no picks)
      // and "All" is encoded by omitting the param.
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'active', label: 'Active' },
        { value: 'marks_locked', label: 'Locked' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'type',
      label: 'Type',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'monthly', label: 'Monthly' },
        { value: 'half_yearly', label: 'Half-yearly' },
        { value: 'annual', label: 'Annual' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      kind: 'date-range',
      key: 'start',
      label: 'Start date',
      group: 'filters',
      params: { from: 'startFrom', to: 'startTo' },
    },
  ],
  sorts: [
    { value: 'startDate:desc', label: 'Latest start' },
    // Registered so the Dates column header can toggle to ascending; an
    // unregistered `field:dir` would silently fall back to the default sort.
    { value: 'startDate:asc', label: 'Earliest start' },
    { value: 'academicYear:desc', label: 'Academic year (newest)' },
    { value: 'createdAt:desc', label: 'Recently created' },
  ],
  resultNoun: ['cycle', 'cycles'],
};

/**
 * Column-header sort bindings, synced to the same `sort` param. Only the Dates
 * column is bound: it sorts on `startDate`, the one field with both directions
 * registered above (academicYear / createdAt stay dropdown-only).
 */
export const EXAMS_SORT_COLUMNS: Record<'startDate', SortColumnBinding> = {
  startDate: { field: 'startDate', firstClickDir: 'desc' },
};

/**
 * URL search-param contract for the Exams list, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/academics/exams` opens on the configured defaults).
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys, so a missing key would be silently dropped on the next
 * read. In particular `start` is the date-range facet's PRESET key (e.g.
 * `last7`), distinct from the concrete custom bounds `startFrom` / `startTo`;
 * omitting it would stop a preset choice from sticking. `academicYear` is the
 * sibling single-value control (not a toolkit facet) and is declared here too
 * so it round-trips through the route boundary. Kept beside the config (not in
 * the route) so this boundary is unit-testable without loading the route's
 * React/router side effects.
 */
export const examsSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  type: z.string().optional(),
  start: z.string().optional(),
  startFrom: z.string().optional(),
  startTo: z.string().optional(),
  academicYear: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
